import React, { useState, useEffect, useCallback } from 'react';
import { OV_STRINGS } from '../../constants/strings';
import { E_RAND_TYPE_LABEL } from '../../../../common/constants/saleConstants';
import * as ovApi from '../../apis/ownerVerificationApi';
import { toast, TOAST_POSITION } from '../../../../components/material/ToastPopup';
import { realPriceColor } from '../../../../utils/color';
import styles from './EstateDetailStep.module.scss';

const CERT_TYPE_ORDER = ['land', 'arch', 'ziphap'];

const CERT_TYPE_INFO = {
  land: { label: '토지 등기부', desc: '토지(지번) 소유자 기준으로 인증합니다.' },
  arch: { label: '건물 등기부', desc: '건물 소유자 기준으로 인증합니다.' },
  ziphap: { label: '집합건물 등기부', desc: '호실별 소유자 기준으로 인증합니다.' },
};

function EstateDetailStep({ estate = {}, onNext, onClose }) {
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState('');
  const [availableCertTypes, setAvailableCertTypes] = useState([]);
  const [certType, setCertType] = useState('');

  const [archList, setArchList] = useState([]);
  const [selectedArch, setSelectedArch] = useState(null);

  const [dongList, setDongList] = useState([]);
  const [floorList, setFloorList] = useState([]);
  const [hoList, setHoList] = useState([]);
  const [selectedDong, setSelectedDong] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('');
  const [selectedHo, setSelectedHo] = useState('');

  const [landAddresses, setLandAddresses] = useState([]);
  const [selectedLandIndex, setSelectedLandIndex] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [bottomSheet, setBottomSheet] = useState(null);

  const pnu = String(estate.pnu || '');
  const typeName = E_RAND_TYPE_LABEL[estate.e_rand_type] || '';


  // ── 초기 로드: 주소 + 가용 등기 유형 조회 ──
  useEffect(() => {
    if (!pnu) return;
    (async () => {
      setLoading(true);
      try {
        const addrData = await ovApi.getAddressByPnu(pnu);
        setAddress(addrData.addr || estate.address || '');

        const metaData = await ovApi.loadCertMetaInfo({
          pnu,
          address: addrData.addr || estate.address || '',
          service: 's_type',
        });

        // API 응답의 s_type은 한글 ("토지", "건물", "집합건물")
        const S_TYPE_MAP = { '토지': 'land', '건물': 'arch', '집합건물': 'ziphap' };
        const types = [];
        if (metaData.data) {
          for (const item of metaData.data) {
            const key = S_TYPE_MAP[item.s_type];
            if (key && !types.includes(key)) types.push(key);
          }
        }

        if (types.length === 0) {
          types.push('land', 'arch', 'ziphap');
        }

        setAvailableCertTypes(types);
      } catch (e) {
        console.error('[OV] meta load failed:', e);
        setAddress(estate.address || '');
        setAvailableCertTypes(['land', 'arch', 'ziphap']);
      } finally {
        setLoading(false);
      }
    })();
  }, [pnu, estate.address]);


  // ── certType 변경 시: 하위 선택지 초기화 + 데이터 로드 ──
  useEffect(() => {
    if (!certType || !pnu) return;

    setArchList([]);
    setSelectedArch(null);
    setDongList([]);
    setFloorList([]);
    setHoList([]);
    setSelectedDong('');
    setSelectedFloor('');
    setSelectedHo('');
    setLandAddresses([]);
    setSelectedLandIndex(null);

    if (certType === 'arch') loadArchList();
    else if (certType === 'ziphap') loadDongList();
    else if (certType === 'land') loadLandAddresses();
  }, [certType]);

  const loadArchList = useCallback(async () => {
    try {
      const data = await ovApi.loadCertMetaInfo({ pnu, address, service: 'arch' });
      const list = data.data || [];
      setArchList(list);
      if (list.length === 1) setSelectedArch(list[0]);
    } catch { setArchList([]); }
  }, [pnu, address]);

  const loadDongList = useCallback(async () => {
    try {
      const data = await ovApi.loadCertMetaInfo({ pnu, address, service: 'ziphap_detail', selector: 'dong' });
      setDongList(data.data || []);
    } catch { setDongList([]); }
  }, [pnu, address]);

  const loadFloorList = useCallback(async (dong) => {
    try {
      const data = await ovApi.loadCertMetaInfo({ pnu, address, service: 'ziphap_detail', selector: 'floor', dong });
      setFloorList(data.data || []);
    } catch { setFloorList([]); }
  }, [pnu, address]);

  const loadHoList = useCallback(async (dong, floor) => {
    try {
      const data = await ovApi.loadCertMetaInfo({ pnu, address, service: 'ziphap_detail', selector: 'ho', dong, floor });
      setHoList(data.data || []);
    } catch { setHoList([]); }
  }, [pnu, address]);

  const loadLandAddresses = useCallback(async () => {
    try {
      const data = await ovApi.loadCertMetaInfo({ pnu, address, service: 'land' });
      const list = data.data || [];
      setLandAddresses(list);
      if (list.length === 1) setSelectedLandIndex(0);
    } catch { setLandAddresses([]); }
  }, [pnu, address]);

  const handleDongChange = (dong) => {
    setSelectedDong(dong);
    setSelectedFloor('');
    setSelectedHo('');
    setFloorList([]);
    setHoList([]);
    if (dong) loadFloorList(dong);
  };

  const handleFloorChange = (floor) => {
    setSelectedFloor(floor);
    setSelectedHo('');
    setHoList([]);
    if (selectedDong && floor) loadHoList(selectedDong, floor);
  };


  // ── 유효성 ──
  const isValid = (() => {
    if (!certType) return false;
    if (certType === 'land') return selectedLandIndex != null;
    if (certType === 'arch') return selectedArch != null;
    if (certType === 'ziphap') return selectedDong && selectedFloor && selectedHo;
    return false;
  })();


  // ── 제출 ──
  const handleNext = async () => {
    setSubmitting(true);
    try {
      const detail = { cert_type: certType, pnu, address };

      if (certType === 'land' && selectedLandIndex != null) {
        const selected = landAddresses[selectedLandIndex];
        if (selected?.pin) detail.pin = selected.pin;
      }

      if (certType === 'arch' && selectedArch) {
        detail.pin = selectedArch.pin;
        detail.building_name = selectedArch.addr || '';
      }

      if (certType === 'ziphap') {
        detail.dong = selectedDong;
        detail.floor = selectedFloor;
        detail.ho = selectedHo;
        const data = await ovApi.loadCertMetaInfo({
          pnu, address, service: 'ziphap',
          dong: selectedDong, floor: selectedFloor, ho: selectedHo,
        });
        if (data.data?.[0]?.pin) detail.pin = data.data[0].pin;
      }

      onNext(detail);
    } catch (e) {
      console.error('[OV] PIN lookup failed:', e);
      toast.error('정보 조회에 실패했습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
    } finally {
      setSubmitting(false);
    }
  };


  const isMobile = () => window.innerWidth <= 768;

  const openSheet = (title, items, onSelect) => {
    setBottomSheet({ title, items, onSelect });
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  // 하위 셀렉터 필요 여부
  const showZiphapSelector = certType === 'ziphap';
  const showArchSelector = certType === 'arch' && archList.length > 1;
  const showLandSelector = certType === 'land' && landAddresses.length > 1;


  return (
    <>
      <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }} />
      <div className={styles.modalContent}>
        <div className={styles.modal}>

          {/* 네비게이션 */}
          <div className={styles.nav}>
            <span className={styles.badge}>등기선택</span>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* 타이틀 */}
          <div className={styles.header}>
            <h2 className={styles.title}>인증에 사용할 등기를 선택해주세요</h2>
            <p className={styles.desc}>선택한 등기의 소유자 정보와 소유자 본인인증 결과를 대조합니다.</p>
          </div>

          {/* 매물 정보 카드 */}
          <div className={styles.estateInfoWrap}>
            <div className={styles.estateInfo}>
              {typeName && <span className={styles.estateTypeBadge} style={realPriceColor(typeName)}>{typeName}</span>}
              <span className={styles.estateAddress}>{address || estate.address || ''}</span>
            </div>
          </div>

          {/* 본문 (스크롤) */}
          <div className={styles.body}>
            {loading ? (
              <div className={styles.loadingText}>주소 정보를 불러오고 있습니다</div>
            ) : (
              <>
                {/* 등기 유형 선택 */}
                <div className={styles.section}>
                  <div className={styles.sectionLabel}>
                    <span>등기 유형</span>
                    <span className={styles.required}>*</span>
                  </div>
                  <div className={styles.certTypeCards}>
                    {CERT_TYPE_ORDER.filter(t => availableCertTypes.includes(t)).map((type) => {
                      const info = CERT_TYPE_INFO[type];
                      const selected = certType === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          className={`${styles.certCard} ${selected ? styles.certCardSelected : ''}`}
                          onClick={() => setCertType(type)}
                        >
                          <div className={styles.certCardHeader}>
                            <span className={`${styles.radioCircle} ${selected ? styles.radioCircleSelected : ''}`} />
                            <span className={`${styles.certCardLabel} ${selected ? styles.certCardLabelSelected : ''}`}>
                              {info.label}
                            </span>
                          </div>
                          <p className={styles.certCardDesc}>{info.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 집합건물: 동/층/호 선택 */}
                {showZiphapSelector && (
                  <div className={styles.section}>
                    <div className={styles.sectionLabel}>
                      <span>동 / 층 / 호 선택</span>
                      <span className={styles.required}>*</span>
                    </div>
                    <div className={styles.selectRow}>
                      <div className={styles.dropdownWrap}>
                        <button
                          type="button"
                          className={`${styles.dropdownBtn} ${openDropdown === 'dong' ? styles.dropdownBtnOpen : ''}`}
                          onClick={() => {
                            if (isMobile()) {
                              openSheet('동 선택', dongList.map(d => ({ label: d.dong, value: d.dong })), (v) => handleDongChange(v));
                            } else {
                              toggleDropdown('dong');
                            }
                          }}
                        >
                          <span className={styles.dropdownLabel}>{selectedDong || '동'}</span>
                          <svg className={`${styles.dropdownChevron} ${openDropdown === 'dong' ? styles.dropdownChevronUp : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 8l5 5 5-5" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        {openDropdown === 'dong' && (
                          <div className={styles.dropdownMenu}>
                            {dongList.map((d, i) => (
                              <button
                                key={i} type="button"
                                className={`${styles.dropdownItem} ${selectedDong === d.dong ? styles.dropdownItemSelected : ''}`}
                                onClick={() => { handleDongChange(d.dong); setOpenDropdown(null); }}
                              >{d.dong}</button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={styles.dropdownWrap}>
                        <button
                          type="button"
                          className={`${styles.dropdownBtn} ${openDropdown === 'floor' ? styles.dropdownBtnOpen : ''}`}
                          onClick={() => {
                            if (!selectedDong) return;
                            if (isMobile()) {
                              openSheet('층 선택', floorList.map(f => ({ label: f.floor, value: f.floor })), (v) => handleFloorChange(v));
                            } else {
                              toggleDropdown('floor');
                            }
                          }}
                          disabled={!selectedDong}
                        >
                          <span className={styles.dropdownLabel}>{selectedFloor || '층'}</span>
                          <svg className={`${styles.dropdownChevron} ${openDropdown === 'floor' ? styles.dropdownChevronUp : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 8l5 5 5-5" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        {openDropdown === 'floor' && (
                          <div className={styles.dropdownMenu}>
                            {floorList.map((f, i) => (
                              <button
                                key={i} type="button"
                                className={`${styles.dropdownItem} ${selectedFloor === f.floor ? styles.dropdownItemSelected : ''}`}
                                onClick={() => { handleFloorChange(f.floor); setOpenDropdown(null); }}
                              >{f.floor}</button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className={styles.dropdownWrap}>
                        <button
                          type="button"
                          className={`${styles.dropdownBtn} ${openDropdown === 'ho' ? styles.dropdownBtnOpen : ''}`}
                          onClick={() => {
                            if (!selectedFloor) return;
                            if (isMobile()) {
                              openSheet('호 선택', hoList.map(h => ({ label: h.ho, value: h.ho })), (v) => setSelectedHo(v));
                            } else {
                              toggleDropdown('ho');
                            }
                          }}
                          disabled={!selectedFloor}
                        >
                          <span className={styles.dropdownLabel}>{selectedHo || '호'}</span>
                          <svg className={`${styles.dropdownChevron} ${openDropdown === 'ho' ? styles.dropdownChevronUp : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M5 8l5 5 5-5" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        {openDropdown === 'ho' && (
                          <div className={styles.dropdownMenu}>
                            {hoList.map((h, i) => (
                              <button
                                key={i} type="button"
                                className={`${styles.dropdownItem} ${selectedHo === h.ho ? styles.dropdownItemSelected : ''}`}
                                onClick={() => { setSelectedHo(h.ho); setOpenDropdown(null); }}
                              >{h.ho}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 건물 등기부: 건물 여러개일 때 선택 */}
                {showArchSelector && (
                  <div className={styles.section}>
                    <div className={styles.sectionLabel}>
                      <span>건물 선택</span>
                      <span className={styles.required}>*</span>
                    </div>
                    <div className={styles.dropdownWrap}>
                      <button
                        type="button"
                        className={`${styles.dropdownBtn} ${openDropdown === 'arch' ? styles.dropdownBtnOpen : ''}`}
                        onClick={() => {
                          if (isMobile()) {
                            openSheet('건물 선택', archList.map((a, i) => ({ label: a.addr || a.pin, value: i })), (v) => setSelectedArch(archList[v]));
                          } else {
                            toggleDropdown('arch');
                          }
                        }}
                      >
                        <span className={styles.dropdownLabel}>{selectedArch ? (selectedArch.addr || selectedArch.pin) : '건물 선택'}</span>
                        <svg className={`${styles.dropdownChevron} ${openDropdown === 'arch' ? styles.dropdownChevronUp : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M5 8l5 5 5-5" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {openDropdown === 'arch' && (
                        <div className={styles.dropdownMenu}>
                          {archList.map((a, i) => (
                            <button
                              key={i} type="button"
                              className={`${styles.dropdownItem} ${selectedArch === a ? styles.dropdownItemSelected : ''}`}
                              onClick={() => { setSelectedArch(a); setOpenDropdown(null); }}
                            >{a.addr || a.pin}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 토지: 주소 여러개일 때 선택 */}
                {showLandSelector && (
                  <div className={styles.section}>
                    <div className={styles.sectionLabel}>
                      <span>주소 선택</span>
                      <span className={styles.required}>*</span>
                    </div>
                    <div className={styles.dropdownWrap}>
                      <button
                        type="button"
                        className={`${styles.dropdownBtn} ${openDropdown === 'land' ? styles.dropdownBtnOpen : ''}`}
                        onClick={() => {
                          if (isMobile()) {
                            openSheet('주소 선택', landAddresses.map((a, i) => ({ label: a.addr || a.address || `주소 ${i + 1}`, value: i })), (v) => setSelectedLandIndex(v));
                          } else {
                            toggleDropdown('land');
                          }
                        }}
                      >
                        <span className={styles.dropdownLabel}>{selectedLandIndex != null ? (landAddresses[selectedLandIndex]?.addr || `주소 ${selectedLandIndex + 1}`) : '주소 선택'}</span>
                        <svg className={`${styles.dropdownChevron} ${openDropdown === 'land' ? styles.dropdownChevronUp : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M5 8l5 5 5-5" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {openDropdown === 'land' && (
                        <div className={styles.dropdownMenu}>
                          {landAddresses.map((a, i) => (
                            <button
                              key={i} type="button"
                              className={`${styles.dropdownItem} ${selectedLandIndex === i ? styles.dropdownItemSelected : ''}`}
                              onClick={() => { setSelectedLandIndex(i); setOpenDropdown(null); }}
                            >{a.addr || a.address || `주소 ${i + 1}`}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 하단 버튼 */}
          <div className={styles.actionArea}>
            <button
              type="button"
              className={`${styles.nextButton} ${isValid ? styles.active : ''}`}
              disabled={!isValid || loading || submitting}
              onClick={handleNext}
            >
              {OV_STRINGS.NEXT}
            </button>
          </div>
        </div>
      </div>

      {/* 모바일 바텀시트 */}
      {bottomSheet && (
        <>
          <div className={styles.sheetOverlay} onClick={() => setBottomSheet(null)} />
          <div className={styles.sheet}>
            <div className={styles.sheetHandle} />
            <div className={styles.sheetHeader}>
              <span className={styles.sheetTitle}>{bottomSheet.title}</span>
              <button type="button" className={styles.sheetClose} onClick={() => setBottomSheet(null)} aria-label="닫기">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className={styles.sheetList}>
              {bottomSheet.items.map((item, i) => (
                <button
                  key={i}
                  type="button"
                  className={styles.sheetItem}
                  onClick={() => {
                    bottomSheet.onSelect(item.value);
                    setBottomSheet(null);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default EstateDetailStep;
